import { useCallback, useMemo, FC, ReactNode } from "react";
import {
  Brand,
  BrandInjectedProps,
  SEGMENT_IN_VIEW_THRESHOLD,
  SEGMENT_PROMOTION_TYPE,
} from "src/segment";
import {
  HomePageSingleBrand,
  ImageBannerData,
  SummaryBrand,
} from "src/types/graphql.d";
import { useInView } from "react-intersection-observer";
import { getFileName } from "src/utils/getFileName";
import { formatName } from "src/utils/formatName";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { useFeatureFlags } from "src/hooks/useFeatureFlags";
import { trackViewedPromotionEvent } from "src/tracking/events/viewed-promotion";
import { trackClickedPromotionEvent } from "src/tracking/events/clicked-promotion";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { trackCarouselItemSelectedEvent } from "src/tracking/events/carousel-item-selected";
import type { CarouselEventBaseProps } from "src/tracking/utils/types";

function isImageBannerData(brand: Brand): brand is ImageBannerData {
  return (
    (brand as ImageBannerData).title !== undefined &&
    ((brand as ImageBannerData).image_desktop !== undefined ||
      (brand as ImageBannerData).image_mobile !== undefined)
  );
}

interface SegmentBrandTrackingProps {
  brand: Brand;
  title?: string;
  children: (props: BrandInjectedProps) => ReactNode;
  cover?: boolean;
  favourite?: boolean;
  position: string;
  tab?: string;
  promotionType: SEGMENT_PROMOTION_TYPE;
  promotionDisplayType?: string;
  carouselTrackingData?: CarouselEventBaseProps & {
    contentType: "brand";
    items: Brand[];
  };
}

const PROMOTION_TYPE = [
  SEGMENT_PROMOTION_TYPE.imageBanners,
  SEGMENT_PROMOTION_TYPE.featuredShop,
];

const SegmentBrandTracking: FC<SegmentBrandTrackingProps> = ({
  brand,
  children,
  cover = false,
  favourite = false,
  position,
  tab,
  promotionType,
  promotionDisplayType,
  carouselTrackingData,
}) => {
  const isLoggedIn = useIsLoggedIn();
  const { enableGatedHomepage } = useFeatureFlags();
  const { currentShopType } = useShopTypeContext();

  const { image, subtitle, discount, name, cover_image } = brand as
    | SummaryBrand
    | HomePageSingleBrand;

  const trackingName = useMemo(() => {
    const promoName: [string, string] = [formatName(name), discount || ""];

    if (favourite) {
      return subtitle as string;
    }

    if (isImageBannerData(brand)) {
      return brand.title || brand.subtitle;
    }

    return promoName.filter((v) => !!v).join(" - ");
  }, [brand, favourite, subtitle, name, discount]);

  const promotionTrackingData = useMemo(() => {
    let creative = getFileName(cover ? cover_image : image);

    if (isImageBannerData(brand)) {
      creative = getFileName(brand.image_desktop);
    }

    return {
      creative,
      name: trackingName,
      position,
      tab,
      promotionType,
      gated: !!(!isLoggedIn && enableGatedHomepage),
      shopType: currentShopType,
      promoDisplayType: promotionDisplayType,
    };
  }, [
    brand,
    cover,
    cover_image,
    image,
    trackingName,
    position,
    tab,
    promotionType,
    isLoggedIn,
    enableGatedHomepage,
    currentShopType,
    promotionDisplayType,
  ]);

  const [ref] = useInView({
    threshold: SEGMENT_IN_VIEW_THRESHOLD,
    triggerOnce: true,
    onChange: (inView) => {
      if (!inView) {
        return;
      }

      if (PROMOTION_TYPE.includes(promotionType)) {
        void trackViewedPromotionEvent({
          ...promotionTrackingData,
        });
      }
    },
  });

  const onClickHandler = useCallback(
    (itemId: string) => {
      if (PROMOTION_TYPE.includes(promotionType)) {
        void trackClickedPromotionEvent({
          ...promotionTrackingData,
        });
        return;
      }

      if (carouselTrackingData) {
        void trackCarouselItemSelectedEvent({
          ...carouselTrackingData,
          itemId,
        });
      }
    },
    [promotionTrackingData, carouselTrackingData, promotionType]
  );

  return <div ref={ref}>{children({ onClickHandler })}</div>;
};

export default SegmentBrandTracking;
